import CurrencyInput from 'components/base/currency-input';
import React, { useMemo, useState } from 'react';
import { ReactComponent as DotIcon } from 'assets/icons/dot-icon.svg';
import { formatCurrency } from 'utils/functions';
import moment from 'moment';
import { Dropdown, DropdownList, FileUploader } from '@cokitchen/cokitchen-components';
import { Calendar } from 'react-date-range';
import { ArrowDownIcon } from 'cokitchen-icons';
import { ProcurementItemType } from 'models/procurement.model';
import { FILE_UPLOADER_TOKEN } from 'utils/constants';

export type receiveProcurementItemsPayload = {
  id: string;
  quantity_received: number;
  price_per_unit: number;
  expiration_date: string | null;
  invoice_url?: string;
};

interface MainstoreRequestItemReceiveProps {
  item: ProcurementItemType;
  onItemChange: (updatedItem: receiveProcurementItemsPayload) => void;
}

const ProcurementRequestItemReceive = ({
  item,
  onItemChange
}: MainstoreRequestItemReceiveProps) => {
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [showDateModal, setShowDateModal] = useState(false);

  const defaultExpirationDate = useMemo(() => {
    const shelfLifeDays = item?.ingredient?.shelf_life ?? 0;

    if (shelfLifeDays <= 0) return null;

    const today = new Date();
    const expirationDate = new Date(today);
    expirationDate.setDate(today.getDate() + shelfLifeDays);

    return expirationDate.toDateString();
  }, [item?.ingredient?.shelf_life]);

  const [localItem, setLocalItem] = useState({
    id: item.id,
    quantity_received: 0,
    price_per_unit: 0,
    expiration_date: defaultExpirationDate,
    invoice_url: undefined
  });

  const nextBatchNo = useMemo(() => {
    return item?.batches ? Math.max(...item.batches.map((b) => Number(b.batch_no)), 0) + 1 : 1;
  }, [item?.batches]);
  const changeTotalPrice = (value: string) => {
    setTotalPrice(Number(value));
    const updatedItem = {
      ...localItem,
      price_per_unit: Number((Number(value) / Number(localItem.quantity_received)).toFixed(2))
    };
    setLocalItem(updatedItem);
    onItemChange(updatedItem);
  };
  const changeReceivedQuantity = (value: string) => {
    if (Number(value) === localItem.quantity_received) return;

    setErrors({
      ...errors,
      [item.ingredient_id]: {
        quantity_received: ''
      }
    });
    // Update local state with new quantity_received
    const updatedItem = {
      ...localItem,
      quantity_received: Number(value),
      price_per_unit: Number((totalPrice / Number(value)).toFixed(5))
    };
    setLocalItem(updatedItem);
    onItemChange(updatedItem);
  };
  const totalQuantityReceived = useMemo(() => {
    return (
      ' ' +
      Math.ceil(Number(item.quantity_received || 0) + Number(localItem.quantity_received || 0)) +
      ' ' +
      item?.ingredient?.unit_type
    );
  }, [item.quantity_received, localItem.quantity_received]);

  return (
    <div className='mx-8'>
      <div className='flex justify-between font-medium '>
        <div key={item.id} className='flex gap-2  items-center mb-5'>
          <p className=''>{item?.ingredient?.name}</p>
          <DotIcon />
          <p>{item?.vendor?.name}</p>
          <DotIcon />
          <p className='text-blue-5'>
            {item.quantity} {item.ingredient.unit_type} requested
          </p>
        </div>
        <p>Batch {nextBatchNo}</p>
      </div>
      <div className=' font-400 text-sm w-full grid grid-cols-2 mb-6 gap-x-[28px] gap-y-6'>
        <div>
          <CurrencyInput
            label='Quantity received'
            placeholder='00'
            name='quantity_received'
            hasUnit
            unitValue={item?.ingredient?.unit_type}
            disableUnit
            hideCurrency
            value={localItem.quantity_received}
            onChange={changeReceivedQuantity}
            error={errors?.[item.ingredient_id]?.quantity_received}
          />
          <p className='text-sm'>
            Total Quantity received:
            <span className='text-blue-5  font-medium'>{totalQuantityReceived}</span>
          </p>
        </div>

        <div>
          <CurrencyInput
            label='Actual price (Total)'
            placeholder='Actual price (Total)'
            name='price'
            value={totalPrice}
            onChange={changeTotalPrice}
          />
          <p>
            Price per unit:
            <span
              className={` font-medium ${localItem.price_per_unit ? 'text-blue-5' : 'text-red-5'}`}>
              {localItem.price_per_unit
                ? formatCurrency(localItem.price_per_unit, { maximumFractionDigits: 5 })
                : ' 0'}
            </span>
          </p>
        </div>
        <div className='flex flex-col gap-1'>
          <span>Expiry Date</span>
          <Dropdown
            visible={showDateModal}
            onClickOutside={() => setShowDateModal(false)}
            content={
              <div
                className='w-full flex justify-between bg-gray-1/20 py-3.5 px-2 border-b-2 border-b-gray-3/60'
                onClick={() => setShowDateModal(!showDateModal)}>
                <p className={`text-sm ${localItem.expiration_date ? '' : 'text-gray-5'}`}>
                  {localItem.expiration_date && localItem.expiration_date !== 'null'
                    ? moment(localItem.expiration_date).format(' DD/MM/YY')
                    : 'Select a date (MM/YY)'}
                </p>
                <ArrowDownIcon className='w-5 h-5' />
              </div>
            }>
            <DropdownList className='!p-0 !max-h-fit'>
              <Calendar
                date={
                  localItem.expiration_date
                    ? moment(localItem.expiration_date).toDate()
                    : new Date()
                }
                onChange={(date: Date) => {
                  const updatedItem = {
                    ...localItem,
                    expiration_date: date.toDateString()
                  };
                  setLocalItem(updatedItem);
                  onItemChange(updatedItem);
                  setShowDateModal(false);
                }}
                showDateDisplay={() => setShowDateModal(true)}
                minDate={new Date()}
                className='calendar'
              />
            </DropdownList>
          </Dropdown>
        </div>
        <div className=''>
          <label>Upload invoice(s)</label>
          <FileUploader
            fileUrls={localItem?.invoice_url ? [localItem?.invoice_url] : []}
            setFileUrls={(imageUrls: any) => {
              const updatedItem = {
                ...localItem,
                invoice_url: imageUrls[0]
              };
              setLocalItem(updatedItem);
              onItemChange(updatedItem);
            }}
            token={FILE_UPLOADER_TOKEN}
            className='w-full border-none p-0 m-0'
          />
        </div>
      </div>
    </div>
  );
};

export default ProcurementRequestItemReceive;
